import { apiClient } from "@/src/api/apiClient";
import { axiosInstance } from "@/src/api/axiosInstance";
import { SUBSCRIPTION_ENDPOINTS } from "@/src/api/endpoints";
import { API_CONFIG } from "@/src/api/apiConfig";
import {
  normalizeInvoicesList,
  normalizePlanFromApi,
  subscriptionApiPath,
} from "@/src/lib/subscriptionPlanMappers";
import type {
  BillingPortalBody,
  BillingPortalResponse,
  CancelSubscriptionBody,
  ChangePlanBody,
  CheckoutBody,
  CheckoutResponse,
  HomeownerPlan,
  HomeownerPlansResponse,
  InvoiceItem,
  InvoicesListResponse,
  SubscriptionMeResponse,
  SubscriptionPlanConfig,
} from "@/src/types/subscriptionPlan.types";
import { ApiClientError } from "@/src/utils/apiError";

export async function fetchSubscriptionMe(): Promise<SubscriptionMeResponse> {
  return apiClient.get<SubscriptionMeResponse>(SUBSCRIPTION_ENDPOINTS.ME);
}

export async function fetchHomeownerPlans(): Promise<HomeownerPlan[]> {
  const data = await apiClient.get<HomeownerPlansResponse>(SUBSCRIPTION_ENDPOINTS.PLANS);
  return (data.plans ?? []).map((p) => normalizePlanFromApi(p as SubscriptionPlanConfig & { key?: string }));
}

export async function startTrial(): Promise<SubscriptionMeResponse> {
  return apiClient.post<SubscriptionMeResponse>(SUBSCRIPTION_ENDPOINTS.START_TRIAL, {});
}

export async function createCheckout(body: CheckoutBody): Promise<CheckoutResponse> {
  return apiClient.post<CheckoutResponse>(SUBSCRIPTION_ENDPOINTS.CHECKOUT, body);
}

export async function openBillingPortal(returnUrl?: string): Promise<BillingPortalResponse> {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const body: BillingPortalBody = {
    returnUrl: returnUrl ?? `${origin}/subscription`,
  };
  return apiClient.post<BillingPortalResponse>(SUBSCRIPTION_ENDPOINTS.BILLING_PORTAL, body);
}

export async function changeSubscriptionPlan(body: ChangePlanBody): Promise<SubscriptionMeResponse> {
  return apiClient.post<SubscriptionMeResponse>(SUBSCRIPTION_ENDPOINTS.CHANGE_PLAN, body);
}

export async function cancelSubscription(body: CancelSubscriptionBody): Promise<SubscriptionMeResponse> {
  return apiClient.post<SubscriptionMeResponse>(SUBSCRIPTION_ENDPOINTS.CANCEL, body);
}

export async function fetchInvoices(page = 1, limit = 20): Promise<InvoicesListResponse> {
  const raw = await apiClient.get<unknown>(
    `${SUBSCRIPTION_ENDPOINTS.INVOICES}?page=${page}&limit=${limit}`,
  );
  return normalizeInvoicesList(raw);
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function filenameFromContentDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback;
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(header);
  return match?.[1]?.trim() || fallback;
}

/** Download with homeowner auth (Bearer). Uses `downloadUrl` from list or invoice id. */
export async function downloadInvoice(invoice: InvoiceItem): Promise<void> {
  const path = invoice.downloadUrl
    ? subscriptionApiPath(invoice.downloadUrl)
    : SUBSCRIPTION_ENDPOINTS.invoiceDownload(invoice.id);

  const response = await axiosInstance.get(path, {
    responseType: "blob",
  });

  const contentType = String(response.headers["content-type"] ?? "");

  if (contentType.includes("application/json")) {
    const text = await (response.data as Blob).text();
    let payload: unknown;
    try {
      payload = JSON.parse(text) as {
        data?: { url?: string };
        url?: string;
      };
    } catch {
      throw new ApiClientError("Invalid invoice download response.", 500);
    }
    const nested = payload as { data?: { url?: string }; url?: string };
    const redirectUrl = nested.data?.url ?? nested.url;
    if (!redirectUrl) {
      throw new ApiClientError("Invoice download URL missing.", 500);
    }
    const absolute = redirectUrl.startsWith("http")
      ? redirectUrl
      : `${API_CONFIG.baseURL.replace(/\/$/, "")}${subscriptionApiPath(redirectUrl)}`;
    window.open(absolute, "_blank", "noopener,noreferrer");
    return;
  }

  const fallbackName = `${invoice.invoiceNumber || invoice.id}.pdf`;
  const filename = filenameFromContentDisposition(
    response.headers["content-disposition"] as string | undefined,
    fallbackName,
  );
  triggerBlobDownload(response.data as Blob, filename);
}

export function buildCheckoutUrls(): { successUrl: string; cancelUrl: string } {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    successUrl: `${origin}/subscription/success`,
    cancelUrl: `${origin}/subscription/cancelled`,
  };
}
